<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ pageTitle }}</h1>
      </div>
    </div>

    <div class="page-container">
    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="loadData">重試</button>
    </div>

    <div v-else>
      <!-- 當前訂閱狀態 -->
      <div class="info-section">
        <h3>當前訂閱狀態</h3>
        <div v-if="subscriptionData?.subscription" class="info-grid">
          <div class="info-item">
            <span class="info-label">方案名稱：</span>
            <span class="info-value">{{ subscriptionData.subscription.planName || '-' }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">訂閱狀態：</span>
            <span :class="subscriptionData.subscription.neverExpires ? 'badge badge-success' : getSubscriptionStatusClass(subscriptionData.subscription.status)">
              {{ subscriptionData.subscription.neverExpires ? '永久有效' : getSubscriptionStatusText(subscriptionData.subscription.status) }}
            </span>
          </div>
          <div class="info-item">
            <span class="info-label">開始日期：</span>
            <span class="info-value">{{ formatDate(subscriptionData.subscription.startDate) }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">到期日期：</span>
            <span class="info-value">{{ subscriptionData.subscription.neverExpires ? '永久有效' : formatDate(subscriptionData.subscription.endDate) }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">剩餘天數：</span>
            <span class="info-value" :class="subscriptionData.subscription.neverExpires ? '' : getDaysRemainingClass(subscriptionData.subscription.daysRemaining)">
              {{ subscriptionData.subscription.neverExpires
                ? '不限期'
                : (subscriptionData.subscription.daysRemaining !== null && subscriptionData.subscription.daysRemaining !== undefined
                  ? `${subscriptionData.subscription.daysRemaining} 天`
                  : '-') }}
            </span>
          </div>
          <div class="info-item">
            <span class="info-label">付款方式：</span>
            <span class="info-value">{{ subscriptionData.subscription.neverExpires ? '免請款' : (subscriptionData.subscription.paymentType === 'manual' ? '人工收款' : '綠界自動扣款') }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">是否試用：</span>
            <span class="info-value">{{ subscriptionData.subscription.isTrial ? '是' : '否' }}</span>
          </div>
          <div
            v-if="subscriptionData.subscription.status === 'active' && !subscriptionData.subscription.isTrial"
            class="info-item"
          >
            <span class="info-label">自動續約：</span>
            <span class="info-value">{{ subscriptionData.subscription.autoRenew === false ? '已關閉' : '開啟' }}</span>
          </div>
          <div v-if="subscriptionData.subscription.isExpiringSoon" class="info-item warning-item">
            <i class="ri-time-warning-line"></i>
            <span class="warning-text">訂閱即將到期，請及時續費</span>
          </div>
          <div v-if="subscriptionData.subscription.isInGracePeriod" class="info-item warning-item">
            <i class="ri-error-warning-line"></i>
            <span class="warning-text">訂閱已進入寬限期，請盡快續費</span>
          </div>
        </div>

        <div
          v-if="subscriptionData?.subscription && showCancelSubscriptionSection"
          class="subscription-cancel-block"
        >
          <button
            type="button"
            class="btn btn-danger-outline"
            :disabled="cancelling"
            @click="handleCancelSubscription"
          >
            <span v-if="cancelling">處理中...</span>
            <span v-else><i class="ri-close-circle-line"></i> 取消訂閱（不續約）</span>
          </button>
          <p class="cancel-hint">
            取消後仍可使用至 {{ formatDate(subscriptionData.subscription.endDate) }}，到期後不再自動續約或扣款。
          </p>
        </div>

        <div v-if="!subscriptionData?.subscription" class="empty-state">
          <i class="ri-information-line"></i>
          <p>目前沒有訂閱資訊</p>
          <p class="hint">請選購下方訂閱方案以啟用服務</p>
        </div>
      </div>

      <!-- 試用中：升級為正式訂閱 -->
      <div class="info-section trial-upgrade-section" v-if="subscriptionData?.subscription?.isTrial && !subscriptionData.subscription.neverExpires">
        <h3><i class="ri-vip-crown-line"></i> 升級為正式訂閱</h3>
        <div class="trial-upgrade-notice">
          <i class="ri-information-line"></i>
          <span>您目前使用的是試用版。前往綠界完成付款且成功扣款後，試用才會結束並啟用正式訂閱；若未完成付款或付款失敗，試用期仍繼續。</span>
        </div>
        <div v-if="plansLoading" class="loading-state-small">載入方案中...</div>
        <div v-else-if="plans.length === 0" class="empty-state">
          <p>目前沒有可選購的方案</p>
        </div>
        <div v-else>
          <div class="billing-cycle-toggle">
            <button
              :class="['btn-toggle', billingCycle === 'monthly' ? 'active' : '']"
              @click="billingCycle = 'monthly'"
            >月繳</button>
            <button
              :class="['btn-toggle', billingCycle === 'yearly' ? 'active' : '']"
              @click="billingCycle = 'yearly'"
            >年繳（省 2 個月）</button>
          </div>

          <div class="plans-grid">
            <div
              v-for="plan in plans"
              :key="plan.id"
              :class="['plan-card', selectedPlanId === plan.id ? 'selected' : '']"
              @click="selectedPlanId = plan.id"
            >
              <div class="plan-check" v-if="selectedPlanId === plan.id">
                <i class="ri-check-line"></i>
              </div>
              <div class="plan-name">{{ plan.plan_name }}</div>
              <div class="plan-price">
                <span class="price-amount">NT$ {{ formatCurrency(billingCycle === 'yearly' ? (plan.yearly_price || plan.monthly_price * 12) : plan.monthly_price) }}</span>
                <span class="price-period">/ {{ billingCycle === 'yearly' ? '年' : '月' }}</span>
              </div>
              <div v-if="plan.description" class="plan-description">{{ plan.description }}</div>
              <div class="plan-detail">
                <div class="detail-section" v-if="plan.modules && plan.modules.length > 0">
                  <div class="detail-section-title">包含功能模組</div>
                  <div class="module-tags">
                    <span v-for="mod in plan.modules" :key="mod.module_code" class="module-tag">
                      <i :class="mod.icon || 'ri-apps-line'"></i>
                      {{ mod.module_name }}
                    </span>
                  </div>
                </div>
                <div class="detail-section">
                  <div class="detail-section-title">方案規格</div>
                  <ul class="feature-list">
                    <li><i class="ri-user-line"></i> 會員數上限：{{ plan.max_members ? Number(plan.max_members).toLocaleString() + ' 人' : '無限制' }}</li>
                    <li><i class="ri-team-line"></i> 員工帳號上限：{{ plan.max_staff_accounts ? plan.max_staff_accounts + ' 個' : '無限制' }}</li>
                    <li><i class="ri-store-line"></i> 門市數上限：{{ plan.max_stores ? plan.max_stores + ' 間' : '無限制' }}</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div class="plan-actions">
            <button
              class="btn btn-success"
              :disabled="!selectedPlanId || upgrading"
              @click="handleUpgrade"
            >
              <span v-if="upgrading">處理中...</span>
              <span v-else><i class="ri-vip-crown-line"></i> 升級並前往綠界付款</span>
            </button>
            <p class="subscribe-hint">點擊後將跳轉至綠界付款；僅在付款成功後試用才會結束</p>
          </div>
        </div>
      </div>

      <!-- 選購訂閱方案（無訂閱或已過期） -->
      <div class="info-section" v-if="!subscriptionData?.subscription?.neverExpires && (!subscriptionData?.subscription || ['expired', 'no_subscription'].includes(subscriptionData.subscription.status))">
        <h3>選購訂閱方案</h3>
        <div v-if="plansLoading" class="loading-state-small">載入方案中...</div>
        <div v-else-if="plans.length === 0" class="empty-state">
          <p>目前沒有可選購的方案</p>
        </div>
        <div v-else>
          <div class="billing-cycle-toggle">
            <button
              :class="['btn-toggle', billingCycle === 'monthly' ? 'active' : '']"
              @click="billingCycle = 'monthly'"
            >月繳</button>
            <button
              :class="['btn-toggle', billingCycle === 'yearly' ? 'active' : '']"
              @click="billingCycle = 'yearly'"
            >年繳（省 2 個月）</button>
          </div>

          <div class="plans-grid">
            <div
              v-for="plan in plans"
              :key="plan.id"
              :class="['plan-card', selectedPlanId === plan.id ? 'selected' : '']"
              @click="selectedPlanId = plan.id"
            >
              <div class="plan-check" v-if="selectedPlanId === plan.id">
                <i class="ri-check-line"></i>
              </div>
              <div class="plan-name">{{ plan.plan_name }}</div>
              <div class="plan-price">
                <span class="price-amount">NT$ {{ formatCurrency(billingCycle === 'yearly' ? (plan.yearly_price || plan.monthly_price * 12) : plan.monthly_price) }}</span>
                <span class="price-period">/ {{ billingCycle === 'yearly' ? '年' : '月' }}</span>
              </div>
              <div v-if="plan.description" class="plan-description">{{ plan.description }}</div>

              <div class="plan-detail">
                <!-- 功能模組 -->
                <div class="detail-section" v-if="plan.modules && plan.modules.length > 0">
                  <div class="detail-section-title">包含功能模組</div>
                  <div class="module-tags">
                    <span v-for="mod in plan.modules" :key="mod.module_code" class="module-tag">
                      <i :class="mod.icon || 'ri-apps-line'"></i>
                      {{ mod.module_name }}
                    </span>
                  </div>
                </div>

                <!-- 規格限制 -->
                <div class="detail-section">
                  <div class="detail-section-title">方案規格</div>
                  <ul class="feature-list">
                    <li>
                      <i class="ri-user-line"></i>
                      會員數上限：{{ plan.max_members ? Number(plan.max_members).toLocaleString() + ' 人' : '無限制' }}
                    </li>
                    <li>
                      <i class="ri-team-line"></i>
                      員工帳號上限：{{ plan.max_staff_accounts ? plan.max_staff_accounts + ' 個' : '無限制' }}
                    </li>
                    <li>
                      <i class="ri-store-line"></i>
                      門市數上限：{{ plan.max_stores ? plan.max_stores + ' 間' : '無限制' }}
                    </li>
                    <li>
                      <i class="ri-chat-1-line"></i>
                      LINE 官方帳號：{{ plan.max_line_configs ? plan.max_line_configs + ' 個' : '無限制' }}
                    </li>
                    <li v-if="plan.allow_api_access">
                      <i class="ri-code-line"></i>
                      支援 API 存取
                    </li>
                    <li v-if="plan.allow_pos_integration">
                      <i class="ri-printer-line"></i>
                      支援 POS 系統整合
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div class="plan-actions">
            <button
              class="btn btn-primary"
              :disabled="!selectedPlanId || subscribing"
              @click="handleSubscribe"
            >
              <span v-if="subscribing">處理中...</span>
              <span v-else>前往綠界付款</span>
            </button>
            <p class="subscribe-hint">點擊後將跳轉至綠界付款頁面，設定信用卡並完成首次扣款後訂閱生效</p>
          </div>
        </div>
      </div>

      <!-- 信用卡記錄 -->
      <div class="info-section">
        <h3>信用卡記錄</h3>
        <div v-if="paymentMethods.length === 0" class="empty-state">
          <p>尚無信用卡記錄</p>
          <p class="hint">完成綠界付款後，系統會自動記錄信用卡末四碼</p>
        </div>
        <div v-else class="payment-methods-list">
          <div v-for="pm in paymentMethods" :key="pm.id" class="payment-method-item">
            <div class="card-icon">
              <i class="ri-bank-card-line"></i>
            </div>
            <div class="card-info">
              <div class="card-number">
                {{ pm.card_type || '信用卡' }} **** **** **** {{ pm.card_last4 || '—' }}
                <span v-if="!pm.card_last4" class="card-last4-missing">（綠界未回傳末四碼）</span>
              </div>
              <div class="card-meta">
                {{ pm.subscription_type === 'main' ? '主訂閱' : '加購方案' }} ·
                新增於 {{ formatDate(pm.created_at) }}
              </div>
            </div>
            <button class="btn btn-danger-outline btn-sm" @click="removePaymentMethod(pm.id)">移除</button>
          </div>
        </div>
      </div>

      <!-- 交易記錄統計 -->
      <div v-if="subscriptionData?.paymentStatistics" class="info-section">
        <h3>交易統計</h3>
        <div class="stats-grid">
          <div class="stat-card success">
            <div class="stat-label">成功交易</div>
            <div class="stat-value">{{ subscriptionData.paymentStatistics.successful_payment_count || 0 }} 次</div>
            <div class="stat-amount">NT$ {{ formatCurrency(subscriptionData.paymentStatistics.successful_total_amount || 0) }}</div>
            <div v-if="subscriptionData.paymentStatistics.last_successful_payment_date" class="stat-date">
              最後成功：{{ formatDate(subscriptionData.paymentStatistics.last_successful_payment_date) }}
            </div>
          </div>
          <div class="stat-card failed">
            <div class="stat-label">失敗交易</div>
            <div class="stat-value">{{ subscriptionData.paymentStatistics.failed_payment_count || 0 }} 次</div>
            <div v-if="subscriptionData.paymentStatistics.last_failed_payment_date" class="stat-date">
              最後失敗：{{ formatDate(subscriptionData.paymentStatistics.last_failed_payment_date) }}
            </div>
          </div>
        </div>
      </div>

      <!-- 交易記錄 -->
      <div class="info-section">
        <h3>交易記錄</h3>
        <div v-if="!subscriptionData?.paymentRecords || subscriptionData.paymentRecords.length === 0" class="empty-state">
          沒有交易記錄
        </div>
        <div v-else class="table-wrapper">
          <table class="detail-table">
            <thead>
              <tr>
                <th>交易日期</th>
                <th>金額</th>
                <th>付款方式</th>
                <th>狀態</th>
                <th>電子發票</th>
                <th>延長天數</th>
                <th>備註</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(record, index) in subscriptionData.paymentRecords"
                :key="index"
                :class="getPaymentRecordRowClass(record.status)"
              >
                <td>{{ formatDate(record.payment_date) }}</td>
                <td>
                  <template v-if="record.status === 'cancel_renewal'">—</template>
                  <template v-else>NT$ {{ formatCurrency(record.amount) }}</template>
                </td>
                <td>{{ record.payment_type === 'manual' ? '人工收款' : '綠界扣款' }}</td>
                <td>
                  <span :class="getPaymentStatusClass(record.status)">
                    {{ getPaymentStatusText(record.status) }}
                  </span>
                </td>
                <td>
                  <div v-if="record.invoice_status === 'allowanced'" class="invoice-info">
                    <span class="badge-invoice allowanced">
                      <i class="ri-exchange-dollar-line"></i> 已折讓
                    </span>
                    <div class="invoice-detail">
                      <span class="invoice-no">{{ record.invoice_number }}</span>
                      <span class="invoice-random" v-if="record.invoice_random_number">
                        驗證碼：{{ record.invoice_random_number }}
                      </span>
                    </div>
                  </div>
                  <div v-else-if="record.invoice_status === 'issued'" class="invoice-info">
                    <span class="badge-invoice issued">
                      <i class="ri-file-paper-2-line"></i> 已開立
                    </span>
                    <div class="invoice-detail">
                      <span class="invoice-no">{{ record.invoice_number }}</span>
                      <span class="invoice-random" v-if="record.invoice_random_number">
                        驗證碼：{{ record.invoice_random_number }}
                      </span>
                    </div>
                  </div>
                  <span v-else-if="record.invoice_status === 'failed'" class="badge-invoice failed">
                    <i class="ri-error-warning-line"></i> 開立失敗
                  </span>
                  <span v-else-if="record.invoice_status === 'void'" class="badge-invoice void">
                    <i class="ri-close-circle-line"></i> 已作廢
                  </span>
                  <span v-else-if="record.payment_type === 'ecpay'" class="badge-invoice pending">
                    <i class="ri-time-line"></i> 未開立
                  </span>
                  <span v-else class="text-muted">—</span>
                </td>
                <td>{{ record.extended_days || '-' }} 天</td>
                <td>
                  <span v-if="record.notes" :title="record.notes">{{ record.notes }}</span>
                  <span v-else-if="record.status === 'failed' && record.ecpay_response" class="text-danger">
                    失敗原因：{{ extractFailureReason(record.ecpay_response) }}
                  </span>
                  <span v-else class="text-muted">-</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- 訂閱歷史 -->
      <div v-if="subscriptionData?.subscriptionHistory && subscriptionData.subscriptionHistory.length > 0" class="info-section">
        <h3>訂閱歷史</h3>
        <p class="table-scroll-hint">欄位較多時請左右滑動，即可看到「綠界訂單編號」。</p>
        <div class="table-wrapper">
          <table class="detail-table detail-table--subscription-history">
            <thead>
              <tr>
                <th>方案</th>
                <th>開始日期</th>
                <th>結束日期</th>
                <th>狀態</th>
                <th>付款方式</th>
                <th title="MerchantTradeNo，建立綠界訂單時寫入">綠界訂單編號</th>
                <th>是否試用</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(sub, index) in subscriptionData.subscriptionHistory" :key="index">
                <td>{{ sub.plan_name || '-' }}</td>
                <td>{{ formatDate(sub.start_date) }}</td>
                <td>{{ formatDate(sub.end_date) }}</td>
                <td>
                  <span :class="getSubscriptionRecordStatusClass(sub.status)">
                    {{ getSubscriptionRecordStatusText(sub.status) }}
                  </span>
                </td>
                <td>{{ sub.payment_type === 'manual' ? '人工收款' : '綠界扣款' }}</td>
                <td>
                  <code v-if="getSubscriptionMerchantTradeNo(sub)" class="merchant-trade-no">{{ getSubscriptionMerchantTradeNo(sub) }}</code>
                  <span v-else class="text-muted">-</span>
                </td>
                <td>{{ sub.is_trial ? '是' : '否' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- ECPay 付款表單（隱藏，自動提交） -->
    <form ref="ecpayForm" :action="ecpayPaymentUrl" method="POST" target="_self" style="display:none">
      <input v-for="(value, key) in ecpayOrderData" :key="key" type="hidden" :name="key" :value="value" />
    </form>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { subscriptionService } from '@/api/modules/subscription.js';
import { showError, showSuccess, showConfirm } from '@/utils/swal.js';
import apiClient from '@/api/axios.js';

export default {
  name: 'ClientSubscription',
  setup() {
    const { pageTitle } = usePageTitle('訂閱與交易記錄');
    const subscriptionData = ref(null);
    const loading = ref(false);
    const error = ref(null);

    const plans = ref([]);
    const plansLoading = ref(false);
    const selectedPlanId = ref(null);
    const expandedPlanIds = ref(new Set());
    const billingCycle = ref('monthly');
    const subscribing = ref(false);
    const upgrading = ref(false);
    const cancelling = ref(false);

    const showCancelSubscriptionSection = computed(() => {
      const s = subscriptionData.value?.subscription;
      if (!s) return false;
      if (s.neverExpires) return false;
      if (s.status !== 'active') return false;
      if (s.isTrial) return false;
      return s.autoRenew !== false;
    });

    const handleCancelSubscription = async () => {
      const confirmed = await showConfirm(
        '取消訂閱自動續約',
        '確定要取消嗎？取消後目前方案仍可使用至訂閱到期日，到期後將不再自動續約或扣款。'
      );
      if (!confirmed) return;
      cancelling.value = true;
      try {
        await subscriptionService.cancelMySubscription();
        await showSuccess('已更新', '已取消自動續約');
        await loadData();
      } catch (err) {
        await showError('取消失敗', err.response?.data?.message || err.message || '請稍後再試');
      } finally {
        cancelling.value = false;
      }
    };

    const paymentMethods = ref([]);

    const ecpayForm = ref(null);
    const ecpayPaymentUrl = ref('');
    const ecpayOrderData = reactive({});

    const loadData = async () => {
      loading.value = true;
      error.value = null;

      try {
        const [subResponse, pmResponse] = await Promise.allSettled([
          subscriptionService.getMySubscription(),
          apiClient.get('/client/payment-methods')
        ]);

        if (subResponse.status === 'fulfilled' && subResponse.value?.success && subResponse.value?.data) {
          subscriptionData.value = subResponse.value.data;
        } else {
          error.value = subResponse.value?.message || '獲取訂閱資訊失敗';
        }

        if (pmResponse.status === 'fulfilled' && pmResponse.value?.success && pmResponse.value?.data) {
          paymentMethods.value = pmResponse.value.data.paymentMethods || [];
        }
      } catch (err) {
        console.error('獲取訂閱資訊失敗:', err);
        error.value = err.message || '獲取訂閱資訊失敗';
        await showError('錯誤', error.value);
      } finally {
        loading.value = false;
      }

      await loadPlans();

      // 綠界首期結果由 Webhook 非同步寫入；ClientBackURL 返回時常尚未寫入 DB，拉長輪詢
      const sub = subscriptionData.value?.subscription;
      if (sub && sub.status === 'pending') {
        const poll = async () => {
          for (let i = 0; i < 20; i++) {
            await new Promise((r) => setTimeout(r, 2500));
            try {
              const res = await subscriptionService.getMySubscription();
              if (res?.success && res?.data) {
                subscriptionData.value = res.data;
                if (res.data?.subscription?.status !== 'pending') break;
              }
            } catch (_) {
              break;
            }
          }
        };
        void poll();
      }
    };

    const loadPlans = async () => {
      plansLoading.value = true;
      try {
        const res = await apiClient.get('/client-subscriptions/plans');
        if (res?.success && res?.data) {
          plans.value = res.data.plans || [];
          expandedPlanIds.value = new Set(plans.value.map(p => p.id));
        }
      } catch (err) {
        console.error('載入方案失敗:', err);
      } finally {
        plansLoading.value = false;
      }
    };

    const togglePlanDetail = (planId) => {
      const next = new Set(expandedPlanIds.value);
      if (next.has(planId)) {
        next.delete(planId);
      } else {
        next.add(planId);
      }
      expandedPlanIds.value = next;
    };

    const handleSubscribe = async () => {
      if (!selectedPlanId.value) return;

      subscribing.value = true;
      try {
        const res = await apiClient.post('/client-subscriptions/initiate', {
          planId: selectedPlanId.value,
          billingCycle: billingCycle.value
        });

        if (res?.success && res?.data) {
          const { paymentUrl, orderData } = res.data;

          // 填入隱藏表單並提交到 ECPay
          ecpayPaymentUrl.value = paymentUrl;
          Object.keys(ecpayOrderData).forEach(k => delete ecpayOrderData[k]);
          Object.assign(ecpayOrderData, orderData);

          await showSuccess('準備付款', `即將跳轉至綠界付款頁面，請在付款頁面輸入信用卡資訊完成 ${res.data.planName} 訂閱`);

          // 等待 Vue 渲染後提交表單
          setTimeout(() => {
            ecpayForm.value?.submit();
          }, 300);
        } else {
          await showError('發起訂閱失敗', res?.message || '請稍後再試');
        }
      } catch (err) {
        console.error('發起訂閱失敗:', err);
        await showError('發起訂閱失敗', err.response?.data?.message || err.message || '請稍後再試');
      } finally {
        subscribing.value = false;
      }
    };

    const handleUpgrade = async () => {
      if (!selectedPlanId.value) return;

      const confirmed = await showConfirm(
        '確認升級',
        `將開啟綠界付款頁面。試用期會保留至付款成功；若未完成付款或失敗，您仍可繼續使用試用。確定要繼續嗎？`
      );
      if (!confirmed) return;

      upgrading.value = true;
      try {
        const res = await apiClient.post('/client-subscriptions/me/upgrade', {
          planId: selectedPlanId.value,
          billingCycle: billingCycle.value
        });

        if (res?.success && res?.data) {
          const { paymentUrl, orderData } = res.data;

          ecpayPaymentUrl.value = paymentUrl;
          Object.keys(ecpayOrderData).forEach(k => delete ecpayOrderData[k]);
          Object.assign(ecpayOrderData, orderData);

          await showSuccess('準備付款', `即將跳轉至綠界；付款成功後試用將結束並啟用 ${res.data.planName} 正式訂閱`);

          setTimeout(() => {
            ecpayForm.value?.submit();
          }, 300);
        } else {
          await showError('升級失敗', res?.message || '請稍後再試');
        }
      } catch (err) {
        console.error('升級失敗:', err);
        await showError('升級失敗', err.response?.data?.message || err.message || '請稍後再試');
      } finally {
        upgrading.value = false;
      }
    };

    const removePaymentMethod = async (id) => {
      const confirmed = await showConfirm('確認移除', '確定要移除此信用卡記錄嗎？');
      if (!confirmed) return;

      try {
        await apiClient.delete(`/client/payment-methods/${id}`);
        await showSuccess('已移除', '信用卡記錄已移除');
        paymentMethods.value = paymentMethods.value.filter(pm => pm.id !== id);
      } catch (err) {
        await showError('移除失敗', err.response?.data?.message || err.message);
      }
    };

    const formatDate = (dateString) => {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('zh-TW');
    };

    const formatCurrency = (amount) => {
      if (!amount) return '0';
      return parseFloat(amount).toLocaleString('zh-TW', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      });
    };

    const getSubscriptionStatusClass = (status) => {
      const statusMap = {
        'trial': 'badge badge-info',
        'active': 'badge badge-success',
        'pending': 'badge badge-warning',
        'grace_period': 'badge badge-warning',
        'expired': 'badge badge-danger',
        'no_subscription': 'badge badge-secondary'
      };
      return statusMap[status] || 'badge badge-secondary';
    };

    const getSubscriptionStatusText = (status) => {
      const statusMap = {
        'trial': '試用中',
        'active': '啟用中',
        'pending': '待付款',
        'grace_period': '寬限期',
        'expired': '已過期',
        'no_subscription': '無訂閱'
      };
      return statusMap[status] || status || '未知';
    };

    const getSubscriptionRecordStatusClass = (status) => {
      const statusMap = {
        'active': 'badge badge-success',
        'pending': 'badge badge-warning',
        'expired': 'badge badge-danger',
        'cancelled': 'badge badge-secondary'
      };
      return statusMap[status] || 'badge badge-secondary';
    };

    const getSubscriptionRecordStatusText = (status) => {
      const statusMap = {
        'active': '啟用中',
        'pending': '待開通／待付款',
        'expired': '已過期',
        'cancelled': '已取消'
      };
      return statusMap[status] || status || '未知';
    };

    const getSubscriptionMerchantTradeNo = (sub) => {
      if (!sub || typeof sub !== 'object') return '';
      return sub.ecpay_merchant_trade_no || sub.ecpayMerchantTradeNo || '';
    };

    const getPaymentStatusClass = (status) => {
      const statusMap = {
        'completed': 'badge badge-success',
        'failed': 'badge badge-danger',
        'refunded': 'badge badge-warning',
        'cancel_renewal': 'badge badge-info'
      };
      return statusMap[status] || 'badge badge-secondary';
    };

    const getPaymentStatusText = (status) => {
      const statusMap = {
        'completed': '成功',
        'failed': '失敗',
        'refunded': '已退款',
        'cancel_renewal': '取消續約'
      };
      return statusMap[status] || status || '未知';
    };

    const getPaymentRecordRowClass = (status) => {
      if (status === 'failed') return 'payment-failed';
      if (status === 'cancel_renewal') return 'payment-cancel-renewal';
      return '';
    };

    const getDaysRemainingClass = (days) => {
      if (days === null || days === undefined) return '';
      if (days < 0) return 'text-danger';
      if (days <= 7) return 'text-warning';
      return 'text-success';
    };

    const extractFailureReason = (ecpayResponse) => {
      if (!ecpayResponse) return '未知原因';
      try {
        const response = typeof ecpayResponse === 'string' ? JSON.parse(ecpayResponse) : ecpayResponse;
        return response.RtnMsg || response.message || '未知原因';
      } catch {
        return '未知原因';
      }
    };

    onMounted(() => {
      loadData();
    });

    return {
      pageTitle,
      subscriptionData,
      loading,
      error,
      plans,
      plansLoading,
      selectedPlanId,
      expandedPlanIds,
      billingCycle,
      subscribing,
      upgrading,
      cancelling,
      showCancelSubscriptionSection,
      handleCancelSubscription,
      handleUpgrade,
      togglePlanDetail,
      paymentMethods,
      ecpayForm,
      ecpayPaymentUrl,
      ecpayOrderData,
      loadData,
      handleSubscribe,
      removePaymentMethod,
      formatDate,
      formatCurrency,
      getSubscriptionStatusClass,
      getSubscriptionStatusText,
      getSubscriptionRecordStatusClass,
      getSubscriptionRecordStatusText,
      getSubscriptionMerchantTradeNo,
      getPaymentStatusClass,
      getPaymentStatusText,
      getPaymentRecordRowClass,
      getDaysRemainingClass,
      extractFailureReason
    };
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.page-container {
  padding: 20px;
}

.info-section {
  margin-bottom: 24px;
  background: white;
  padding: 24px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.info-section h3 {
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 20px 0;
  padding-bottom: 12px;
  border-bottom: 2px solid #e0e0e0;
}

.subscription-cancel-block {
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #e8e8e8;
}

.subscription-cancel-block .cancel-hint {
  font-size: 13px;
  color: #666;
  margin: 10px 0 0;
  max-width: 520px;
  line-height: 1.5;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 16px;
}

.info-item {
  display: flex;
  gap: 8px;
  align-items: center;
}

.info-label {
  font-weight: 500;
  color: #666;
  min-width: 100px;
}

.info-value {
  color: #333;
}

.warning-item {
  grid-column: 1 / -1;
  padding: 12px;
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 4px;
  color: #856404;
}

.warning-item i {
  font-size: 20px;
  margin-right: 8px;
}

.warning-text {
  font-weight: 500;
}

/* 試用升級區塊 */
.trial-upgrade-section h3 {
  color: #2e7d32;
}

.trial-upgrade-notice {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 16px;
  background: #e8f5e9;
  border: 1px solid #a5d6a7;
  border-radius: 6px;
  margin-bottom: 20px;
  color: #2e7d32;
  font-size: 14px;
  line-height: 1.5;
}

.trial-upgrade-notice i {
  font-size: 18px;
  flex-shrink: 0;
  margin-top: 1px;
}

.btn-success {
  background: #2e7d32;
  color: white;
}

.btn-success:hover:not(:disabled) {
  background: #1b5e20;
}

/* 方案選購 */
.billing-cycle-toggle {
  display: flex;
  gap: 0;
  margin-bottom: 20px;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  overflow: hidden;
  width: fit-content;
}

.btn-toggle {
  padding: 8px 20px;
  border: none;
  background: white;
  cursor: pointer;
  font-size: 14px;
  color: #666;
  transition: all 0.2s;
}

.btn-toggle.active {
  background: #1976d2;
  color: white;
  font-weight: 600;
}

.plans-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
}

.plan-card {
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
}

.plan-card:hover {
  border-color: #1976d2;
  box-shadow: 0 4px 16px rgba(25, 118, 210, 0.22);
  transform: translateY(-2px);
}

.plan-card.selected {
  border-color: #1976d2;
  background: #e3f2fd;
}

.plan-name {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
}

.plan-price {
  margin-bottom: 8px;
}

.price-amount {
  font-size: 22px;
  font-weight: 700;
  color: #1976d2;
}

.price-period {
  font-size: 13px;
  color: #888;
}

.plan-description {
  font-size: 13px;
  color: #666;
  line-height: 1.5;
}

.plan-check {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 24px;
  height: 24px;
  background: #1976d2;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 14px;
  z-index: 2;
}


.plan-detail {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px dashed #d0d0d0;
  animation: fadeIn 0.2s ease;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.detail-section-title {
  font-size: 12px;
  font-weight: 600;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 8px;
}

.module-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.module-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 9px;
  font-size: 12px;
  color: #1976d2;
  background: #e3f2fd;
  border-radius: 20px;
}

.module-tag i {
  font-size: 13px;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(-4px); }
  to   { opacity: 1; transform: translateY(0); }
}

.feature-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.feature-list li {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #444;
}

.feature-list li i {
  font-size: 15px;
  color: #1976d2;
  flex-shrink: 0;
}

.plan-actions {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
}

.subscribe-hint {
  font-size: 13px;
  color: #888;
  margin: 0;
}

/* 信用卡記錄 */
.payment-methods-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.payment-method-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
}

.card-icon {
  font-size: 28px;
  color: #1976d2;
  width: 40px;
  text-align: center;
}

.card-info {
  flex: 1;
}

.card-number {
  font-size: 15px;
  font-weight: 600;
  color: #333;
}

.card-last4-missing {
  font-size: 12px;
  font-weight: 400;
  color: #888;
  margin-left: 4px;
}

.card-meta {
  font-size: 13px;
  color: #888;
  margin-top: 4px;
}

/* 統計 */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
}

.stat-card {
  padding: 20px;
  border-radius: 8px;
  border: 2px solid;
}

.stat-card.success {
  background: #e8f5e9;
  border-color: #4caf50;
}

.stat-card.failed {
  background: #ffebee;
  border-color: #f44336;
}

.stat-label {
  font-size: 14px;
  color: #666;
  margin-bottom: 8px;
}

.stat-value {
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 4px;
}

.stat-amount {
  font-size: 18px;
  font-weight: 500;
  margin-bottom: 8px;
}

.stat-date {
  font-size: 12px;
  color: #666;
}

/* 表格 */
.table-wrapper {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.table-wrapper table {
  min-width: 800px;
}

.table-wrapper .detail-table--subscription-history {
  min-width: 960px;
}

.table-scroll-hint {
  font-size: 12px;
  color: #666;
  margin: 0 0 8px 0;
}

.merchant-trade-no {
  font-size: 12px;
  word-break: break-all;
  background: #f5f5f5;
  padding: 2px 6px;
  border-radius: 4px;
}

.detail-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 16px;
}

.detail-table th,
.detail-table td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid #e0e0e0;
}

.detail-table th {
  background: #f5f5f5;
  font-weight: 600;
  color: #333;
}

.detail-table tr.payment-failed {
  background: #ffebee;
}

.detail-table tr.payment-cancel-renewal {
  background: #e3f2fd;
}

.detail-table tr:hover {
  background: #f5f5f5;
}

/* 空狀態 */
.empty-state {
  text-align: center;
  padding: 40px;
  color: #999;
}

.empty-state i {
  font-size: 48px;
  margin-bottom: 16px;
  display: block;
}

.hint {
  font-size: 13px;
  color: #aaa;
  margin-top: 8px;
}

.loading-state-small {
  color: #888;
  padding: 20px 0;
}

/* 按鈕 */
.btn {
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-primary {
  background: #1976d2;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #1565c0;
}

.btn-secondary {
  background: #757575;
  color: white;
}

.btn-danger-outline {
  background: transparent;
  color: #dc3545;
  border: 1px solid #dc3545;
}

.btn-danger-outline:hover {
  background: #dc3545;
  color: white;
}

.btn-sm {
  padding: 6px 12px;
  font-size: 13px;
}

/* 狀態標籤 */
.badge {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.badge-info { background: #e3f2fd; color: #1976d2; }
.badge-success { background: #e8f5e9; color: #2e7d32; }
.badge-warning { background: #fff8e1; color: #f57c00; }
.badge-danger { background: #ffebee; color: #c62828; }
.badge-secondary { background: #f5f5f5; color: #666; }

.text-danger { color: #dc3545; }
.text-warning { color: #f57c00; }
.text-success { color: #28a745; }
.text-muted { color: #999; font-style: italic; }

/* 電子發票 */
.invoice-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.badge-invoice {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
}

.badge-invoice.issued  { background: #e8f5e9; color: #2e7d32; }
.badge-invoice.allowanced { background: #e3f2fd; color: #1565c0; }
.badge-invoice.failed  { background: #ffebee; color: #c62828; }
.badge-invoice.void    { background: #f5f5f5; color: #757575; }
.badge-invoice.pending { background: #fff8e1; color: #f57f17; }

.invoice-detail {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.invoice-no {
  font-size: 13px;
  font-weight: 600;
  color: #333;
  letter-spacing: 0.04em;
}

.invoice-random {
  font-size: 11px;
  color: #888;
}

.loading-state,
.error-state {
  text-align: center;
  padding: 40px;
}

.spinner {
  border: 3px solid #f3f3f3;
  border-top: 3px solid #1976d2;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: 0 auto 16px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>
